import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import { API_BASE } from '../config';
import { useFileImport } from '../hooks/useFileImport';
import { buildDocumentContentBody } from '../utils/yjs-content-payload';
import { getDocumentPanelPath, resolveSectionDocumentId } from '../utils/document-panel-paths';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarMenuAction,
  SidebarSeparator,
  useSidebar,
} from './ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ListFilter,
  MoreVertical,
  Plus,
  File,
  Archive,
  BookText,
  Pin,
  PinOff,
  Star,
} from 'lucide-react';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import { RotatingTrashIcon } from './RotatingTrashIcon';
import { PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { cn } from '../lib/utils';
import { getIconComponent } from './IconPicker';
import { handleModifierAwareNavigate } from '../utils/app-navigation';
import { sortProjectExplorerDocuments } from '../utils/sort-project-explorer-documents';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { TierLimitModal } from './TierLimitModal';
import { Link } from './ui/link';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { AddDocumentMenu } from './AddDocumentMenu';
import { CreateProjectNoteDialog } from './CreateProjectNoteDialog';
import { ResourceCreateActions } from './ResourceCreateActions';
import { OPEN_CREATE_PROJECT_NOTE_EVENT } from '../utils/create-project-note-events';
import {
  PROJECT_NOTES_CHANGED_EVENT,
  type ProjectNotesChangedDetail,
} from '../utils/project-notes-events';
import { CountBadge } from './CountBadge';

interface ProjectStructure {
  title: string;
  mainDocumentIcon: string | null;
  mainDocumentId: string | null;
  ownerId: string;
  sharePermission: 'reader' | 'commenter' | 'editor' | 'admin' | null;
}

interface DocumentItem {
  id: string;
  title: string;
  icon: string | null;
  pinned?: boolean;
  projectId: string | null;
  sharedIntoProject?: boolean;
  ownerEmail?: string | null;
  sharePermission?: 'reader' | 'commenter' | 'editor' | null;
}

interface TimelineViewItem {
  id: string;
  name: string;
}

interface ProjectExplorerProps {
  project: ProjectStructure;
  projectDocuments: DocumentItem[];
  setProjectDocuments: React.Dispatch<React.SetStateAction<DocumentItem[]>>;
  onProjectUpdate?: (updatedProject: ProjectStructure) => void;
}

const nestedExplorerMenuSubClassName =
  'm-0 w-full min-w-0 border-0 px-0 py-0.5 [&_[data-sidebar=menu-sub-button]]:pl-8';

function ExplorerLabelTooltip({
  label,
  className,
}: {
  label: string;
  className?: string;
}): React.ReactElement {
  const isMobile = useIsMobile();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('block min-w-0 truncate text-primary', className)}>{label}</span>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={!isMobile}
        className="max-w-[min(24rem,calc(100vw-2rem))] break-words border border-border bg-popover text-popover-foreground shadow-md"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function ExplorerSectionLabel({
  label,
  href,
  isExpanded,
  isSectionActive,
  onExpandedChange,
  onNavigate,
}: {
  label: string;
  href: string;
  isExpanded: boolean;
  isSectionActive: boolean;
  onExpandedChange: (open: boolean) => void;
  onNavigate: (event: React.MouseEvent, href: string) => void;
}): React.ReactElement {
  return (
    <SidebarGroupLabel className="select-none gap-1.5 uppercase hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-sm outline-none ring-sidebar-ring focus-visible:ring-2"
          aria-label={label}
        >
          <ChevronRight
            className={cn(
              '!h-4 !w-4 shrink-0 text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-90'
            )}
          />
        </button>
      </CollapsibleTrigger>
      <button
        type="button"
        className="min-w-0 flex-1 cursor-pointer truncate text-left outline-none ring-sidebar-ring focus-visible:ring-2"
        onClick={(event) => {
          if (!isExpanded) {
            onExpandedChange(true);
          } else if (isSectionActive) {
            onExpandedChange(false);
          }
          onNavigate(event, href);
        }}
      >
        {label}
      </button>
    </SidebarGroupLabel>
  );
}

function ExplorerStatusIconTooltip({
  label,
  children,
}: {
  label: string;
  children: React.ReactElement;
}): React.ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">{children}</span>
      </TooltipTrigger>
      <TooltipContent side="right" align="center">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

interface ProjectExplorerDocumentItemProps {
  doc: DocumentItem;
  projectId: string | undefined;
  mainDocumentId: string | null;
  canEditProject: boolean;
  isActive: boolean;
  onNavigate: (e: React.MouseEvent, href: string) => void;
  onSetAsMainDocument: (documentId: string) => Promise<void>;
  onTogglePinned: (documentId: string, pinned: boolean) => Promise<void>;
  onArchiveDocument: (documentId: string) => Promise<void>;
  onDeleteDocument: (documentId: string) => Promise<void>;
  t: (key: string) => string;
}

function ProjectExplorerDocumentItem({
  doc,
  projectId,
  mainDocumentId,
  canEditProject,
  isActive,
  onNavigate,
  onSetAsMainDocument,
  onTogglePinned,
  onArchiveDocument,
  onDeleteDocument,
  t,
}: ProjectExplorerDocumentItemProps): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const DocIcon = doc.icon ? getIconComponent(doc.icon) : null;
  const documentHref = `/projects/${projectId}/documents/${doc.id}`;
  const permissionLabel =
    doc.sharePermission === 'reader'
      ? t('permission_reader')
      : doc.sharePermission === 'commenter'
        ? t('permission_commenter')
        : doc.sharePermission === 'editor'
          ? t('permission_editor')
          : null;
  const sharedMeta = [doc.ownerEmail, permissionLabel].filter(Boolean).join(' · ');
  const tooltipLabel =
    doc.sharedIntoProject && sharedMeta ? `${doc.title} — ${sharedMeta}` : doc.title;
  const isMainDocument = doc.id === mainDocumentId;
  const showStatusIcon = isMainDocument || !!doc.pinned;
  const hideStatusIconForMenu = canEditProject && menuOpen;

  return (
    <SidebarMenuItem className="relative">
      <SidebarMenuSubButton
        className={cn('cursor-pointer', showStatusIcon && !canEditProject && 'pr-8')}
        isActive={isActive}
        onClick={(e) => onNavigate(e, documentHref)}
        onAuxClick={(e) => onNavigate(e, documentHref)}
      >
        {DocIcon ? (
          <DocIcon className="!h-4 !w-4 shrink-0 !text-secondary-foreground" />
        ) : isMainDocument ? (
          <BookText className="!h-4 !w-4 shrink-0 !text-secondary-foreground" />
        ) : (
          <File className="!h-4 !w-4 shrink-0 !text-secondary-foreground" />
        )}
        <div className="min-w-0 flex-1 overflow-hidden">
          <ExplorerLabelTooltip label={tooltipLabel} />
          {doc.sharedIntoProject && sharedMeta ? (
            <div className="truncate text-[11px] leading-tight text-muted-foreground">
              {sharedMeta}
            </div>
          ) : null}
        </div>
      </SidebarMenuSubButton>
      {showStatusIcon ? (
        <span
          data-sidebar="menu-status"
          className={cn(
            'absolute right-1 top-1/2 mr-1 flex aspect-square w-5 -translate-y-1/2 items-center justify-center',
            canEditProject &&
              'pointer-events-none group-hover/menu-item:opacity-0 group-focus-within/menu-item:opacity-0 max-md:opacity-0',
            hideStatusIconForMenu && 'opacity-0'
          )}
        >
          {isMainDocument ? (
            <ExplorerStatusIconTooltip label={t('main_document') || 'Main document'}>
              <Star className="!h-3.5 !w-3.5 text-muted-foreground" aria-hidden />
            </ExplorerStatusIconTooltip>
          ) : (
            <ExplorerStatusIconTooltip label={t('pinned_document') || 'Pinned document'}>
              <Pin className="!h-3.5 !w-3.5 text-muted-foreground" aria-hidden />
            </ExplorerStatusIconTooltip>
          )}
        </span>
      ) : null}
      {canEditProject && (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuAction iconSize="sm" showOnHover>
              <MoreVertical className="!h-4 !w-4" />
            </SidebarMenuAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!doc.sharedIntoProject && mainDocumentId !== doc.id && (
              <DropdownMenuItem
                onClick={async (e) => {
                  e.stopPropagation();
                  await onSetAsMainDocument(doc.id);
                }}
              >
                {t('set_as_main_document')}
              </DropdownMenuItem>
            )}
            {mainDocumentId !== doc.id && (
              <DropdownMenuItem
                onClick={async (e) => {
                  e.stopPropagation();
                  await onTogglePinned(doc.id, !doc.pinned);
                }}
              >
                {doc.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                {doc.pinned
                  ? t('unpin_document') || 'Unpin document'
                  : t('pin_document') || 'Pin document'}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onArchiveDocument(doc.id);
              }}
            >
              <Archive className="h-4 w-4" />
              {t('archive_document')}
            </DropdownMenuItem>
            {!doc.sharedIntoProject && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteDocument(doc.id);
                }}
                className="group"
              >
                <RotatingTrashIcon className="mr-2" />
                {t('move_to_trash')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </SidebarMenuItem>
  );
}

export const ProjectExplorer: React.FC<ProjectExplorerProps> = ({
  project,
  projectDocuments,
  setProjectDocuments,
  onProjectUpdate,
}) => {
  const params = useParams<{ projectId?: string; documentId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const { canEditProject } = useProjectAccess();
  const { t } = useLocale();
  const { state, isMobile, openMobile, setOpenMobile } = useSidebar();
  const isCollapsed = isMobile ? !openMobile : state === 'collapsed';

  const handleMobileNavigation = (): void => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };
  const [isFilesOpen, setIsFilesOpen] = useState(true);
  const [isDocumentationOpen, setIsDocumentationOpen] = useState(true);
  const [isWorldbuildingOpen, setIsWorldbuildingOpen] = useState(true);
  const [isStructureOpen, setIsStructureOpen] = useState(true);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [hasProjectTags, setHasProjectTags] = useState(false);
  const [notesCount, setNotesCount] = useState(0);
  const [timelineViews, setTimelineViews] = useState<TimelineViewItem[]>([]);
  const { showToast, showErrorToast } = useToast();
  const [tierLimitModalOpen, setTierLimitModalOpen] = useState(false);
  const [tierLimitType, setTierLimitType] = useState<'project' | 'document' | 'resource'>('document');
  const [isCreateNoteOpen, setIsCreateNoteOpen] = useState(false);
  const mainDocumentId = project.mainDocumentId;
  const sectionDocumentId = resolveSectionDocumentId(params.documentId, mainDocumentId);

  const isPathActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const navigateTo = (path: string) => (e: React.MouseEvent): void => {
    handleModifierAwareNavigate(e, navigate, path, {
      afterSameTabNavigate: handleMobileNavigation,
    });
  };

  useEffect(() => {
    const timelinePath = params.projectId
      ? `/projects/${params.projectId}/timeline`
      : null;
    if (!timelinePath || location.pathname !== timelinePath) {
      setIsTimelineOpen(false);
    }
  }, [location.pathname, params.projectId]);
  useEffect(() => {
    const handleProjectTitleUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ projectId: string; title: string }>;
      if (customEvent.detail.projectId === params.projectId && onProjectUpdate) {
        onProjectUpdate({
          ...project,
          title: customEvent.detail.title,
        });
      }
    };

    const handleDocumentTitleUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string; title: string }>;
      setProjectDocuments((prev) =>
        prev
          .map((doc) =>
            doc.id === customEvent.detail.documentId
              ? { ...doc, title: customEvent.detail.title }
              : doc
          )
          .sort((a, b) => a.title.localeCompare(b.title))
      );
    };

    const handleDocumentIconUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string; icon: string | null }>;
      setProjectDocuments((prev) =>
        prev.map((doc) =>
          doc.id === customEvent.detail.documentId
            ? { ...doc, icon: customEvent.detail.icon }
            : doc
        )
      );

      if (customEvent.detail.documentId === project.mainDocumentId && onProjectUpdate) {
        onProjectUpdate({
          ...project,
          mainDocumentIcon: customEvent.detail.icon,
        });
      }
    };

    window.addEventListener('project-title-updated', handleProjectTitleUpdate);
    window.addEventListener('document-title-updated', handleDocumentTitleUpdate);
    window.addEventListener('document-icon-updated', handleDocumentIconUpdate);

    return () => {
      window.removeEventListener('project-title-updated', handleProjectTitleUpdate);
      window.removeEventListener('document-title-updated', handleDocumentTitleUpdate);
      window.removeEventListener('document-icon-updated', handleDocumentIconUpdate);
    };
  }, [params.projectId, project, onProjectUpdate, setProjectDocuments]);

  const fetchTimelineViews = useCallback(async (): Promise<void> => {
    if (!params.projectId || !token) return;
    try {
      const response = await fetch(`${API_BASE}/api/projects/${params.projectId}/timeline-views`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTimelineViews(
          (data.views ?? []).map((v: { id: string; name: string }) => ({ id: v.id, name: v.name }))
        );
      } else {
        setTimelineViews([]);
      }
    } catch {
      setTimelineViews([]);
    }
  }, [params.projectId, token]);

  useEffect(() => {
    fetchTimelineViews();
  }, [fetchTimelineViews]);

  const fetchProjectTags = useCallback(async (): Promise<void> => {
    if (!params.projectId || !token) {
      setHasProjectTags(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/projects/${params.projectId}/tags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const tags = Array.isArray(data.tags) ? data.tags : [];
        setHasProjectTags(tags.length > 0);
      } else {
        setHasProjectTags(false);
      }
    } catch {
      setHasProjectTags(false);
    }
  }, [params.projectId, token]);

  const fetchProjectNotesCount = useCallback(async (): Promise<void> => {
    if (!params.projectId || !token) {
      setNotesCount(0);
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/projects/${params.projectId}/notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNotesCount(Array.isArray(data.notes) ? data.notes.length : 0);
      } else {
        setNotesCount(0);
      }
    } catch {
      setNotesCount(0);
    }
  }, [params.projectId, token]);

  useEffect(() => {
    void fetchProjectTags();
  }, [fetchProjectTags]);

  useEffect(() => {
    void fetchProjectNotesCount();
  }, [fetchProjectNotesCount]);

  useEffect(() => {
    const refreshIfSameProject = (event: Event): void => {
      const detail = (event as CustomEvent<ProjectNotesChangedDetail | { projectId?: string }>).detail;
      if (detail?.projectId === params.projectId) {
        void fetchProjectNotesCount();
      }
    };
    window.addEventListener(PROJECT_NOTES_CHANGED_EVENT, refreshIfSameProject);
    window.addEventListener('note-created', refreshIfSameProject);
    window.addEventListener('note-deleted', refreshIfSameProject);
    window.addEventListener('note-restored', refreshIfSameProject);
    return () => {
      window.removeEventListener(PROJECT_NOTES_CHANGED_EVENT, refreshIfSameProject);
      window.removeEventListener('note-created', refreshIfSameProject);
      window.removeEventListener('note-deleted', refreshIfSameProject);
      window.removeEventListener('note-restored', refreshIfSameProject);
    };
  }, [params.projectId, fetchProjectNotesCount]);

  useEffect(() => {
    const handleTimelineViewSaved = (event: Event): void => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (detail?.projectId === params.projectId) {
        fetchTimelineViews();
      }
    };
    window.addEventListener('timeline-view-saved', handleTimelineViewSaved);
    return () => window.removeEventListener('timeline-view-saved', handleTimelineViewSaved);
  }, [params.projectId, fetchTimelineViews]);

  useEffect(() => {
    const handleResourceUploaded = (event: Event): void => {
      const detail = (event as CustomEvent<{ resourceIds?: string[]; projectId?: string }>).detail;
      if (params.projectId && token) {
        void fetchProjectTags();
      }
      if (detail?.resourceIds?.length && detail?.projectId) {
        const resourceId = detail.resourceIds[detail.resourceIds.length - 1];
        handleMobileNavigation();
        navigate(`/projects/${detail.projectId}/resources/${resourceId}`);
      }
    };

    const handleResourceLimitReached = (): void => {
      setTierLimitType('resource');
      setTierLimitModalOpen(true);
    };

    window.addEventListener('resource-uploaded', handleResourceUploaded);
    window.addEventListener('resource-limit-reached', handleResourceLimitReached);

    return () => {
      window.removeEventListener('resource-uploaded', handleResourceUploaded);
      window.removeEventListener('resource-limit-reached', handleResourceLimitReached);
    };
  }, [params.projectId, token, navigate, fetchProjectTags]);

  useEffect(() => {
    if (!canEditProject || !params.projectId) {
      return;
    }

    const handleOpenCreateNote = (event: Event): void => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (detail?.projectId === params.projectId) {
        setIsCreateNoteOpen(true);
      }
    };

    window.addEventListener(OPEN_CREATE_PROJECT_NOTE_EVENT, handleOpenCreateNote);
    return () => window.removeEventListener(OPEN_CREATE_PROJECT_NOTE_EVENT, handleOpenCreateNote);
  }, [canEditProject, params.projectId]);

  const { handleImportFile, isImportingFile } = useFileImport({
    token: token || '',
    t,
    showToast,
    createDocument: async (title: string, content?: Uint8Array) => {
      if (!params.projectId) {
        throw new Error('Project ID is required');
      }

      const body: { title: string; contentBase64?: string } = { title };
      if (content) {
        Object.assign(body, buildDocumentContentBody(content));
      }

      const response = await fetch(`${API_BASE}/api/projects/${params.projectId}/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to create document');
      }

      return await response.json();
    },
    onSuccess: async (documentId: string, documentTitle: string) => {
      setProjectDocuments((prev) => [
        {
          id: documentId,
          title: documentTitle,
          icon: null,
          projectId: params.projectId || null,
        },
        ...prev,
      ]);
      if (params.projectId) {
        window.dispatchEvent(
          new CustomEvent('document-created', {
            detail: {
              documentId,
              projectId: params.projectId,
              title: documentTitle,
            },
          })
        );
        handleMobileNavigation();
        navigate(`/projects/${params.projectId}/documents/${documentId}`);
      }
    },
  });

  const handleCreateNewDocument = async (): Promise<void> => {
    if (!token || !params.projectId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${params.projectId}/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: t('untitled_document'),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.errorCode === 'DOCUMENT_LIMIT_REACHED') {
          setTierLimitType('document');
          setTierLimitModalOpen(true);
          return;
        }
        throw new Error('Failed to create document');
      }

      const data = await response.json();
      setProjectDocuments((prev) => [
        {
          id: data.id,
          title: data.title,
          icon: null,
          projectId: data.projectId,
        },
        ...prev,
      ]);
      window.dispatchEvent(
        new CustomEvent('document-created', {
          detail: {
            documentId: data.id,
            projectId: params.projectId,
            title: data.title,
          },
        })
      );
      handleMobileNavigation();
      navigate(`/projects/${params.projectId}/documents/${data.id}`);
    } catch (error) {
      console.error('Error creating document:', error);
      alert(t('failed_to_create_document'));
    }
  };

  const handleSetAsMainDocument = async (documentId: string): Promise<void> => {
    if (!token || !params.projectId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${params.projectId}/main-document`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ documentId }),
      });

      if (!response.ok) {
        throw new Error('Failed to set main document');
      }

      const document = projectDocuments.find((doc) => doc.id === documentId);
      if (document && onProjectUpdate) {
        onProjectUpdate({
          ...project,
          mainDocumentId: documentId,
          mainDocumentIcon: document.icon,
        });
      }

      showToast(t('main_document_updated') || 'Main document updated');
    } catch (err) {
      console.error('Error setting main document:', err);
      showErrorToast(t('failed_to_set_main_document') || 'Failed to set main document');
    }
  };

  const handleToggleDocumentPinned = async (documentId: string, pinned: boolean): Promise<void> => {
    if (!token || !params.projectId) {
      return;
    }

    const previousPinned = projectDocuments.find((doc) => doc.id === documentId)?.pinned ?? false;
    setProjectDocuments((prev) =>
      prev.map((doc) => (doc.id === documentId ? { ...doc, pinned } : doc))
    );

    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${params.projectId}/documents/${documentId}/pinned`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pinned }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update document pin');
      }

      window.dispatchEvent(
        new CustomEvent('document-pinned-updated', {
          detail: { documentId, pinned },
        })
      );

      showToast(
        pinned
          ? (t('document_pinned') || 'Document pinned')
          : (t('document_unpinned') || 'Document unpinned')
      );
    } catch (err) {
      console.error('Error updating document pin:', err);
      setProjectDocuments((prev) =>
        prev.map((doc) => (doc.id === documentId ? { ...doc, pinned: previousPinned } : doc))
      );
      showErrorToast(t('failed_to_update_document_pin') || 'Failed to update document pin');
    }
  };

  const handleDeleteTimelineView = async (viewId: string): Promise<void> => {
    if (!token || !params.projectId) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${params.projectId}/timeline-views/${viewId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to delete timeline view');
      setTimelineViews((prev) => prev.filter((v) => v.id !== viewId));
      window.dispatchEvent(new CustomEvent('timeline-views-changed', { detail: { projectId: params.projectId } }));
      showToast(t('timeline_view_deleted') || 'Timeline view deleted');
    } catch (err) {
      console.error('Error deleting timeline view:', err);
      showErrorToast(t('timeline_view_delete_failed') || 'Failed to delete timeline view');
    }
  };

  const handleArchiveDocument = async (documentId: string): Promise<void> => {
    if (!token || !params.projectId) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${params.projectId}/documents/${documentId}/archived`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ archived: true }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to archive document');
      }

      setProjectDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      showToast(t('document_archived') || 'Document archived');

      window.dispatchEvent(new CustomEvent('document-archived', {
        detail: { documentId, projectId: params.projectId },
      }));
    } catch (err) {
      console.error('Error archiving document:', err);
      showErrorToast(t('failed_to_archive_document') || 'Failed to archive document');
    }
  };

  const handleDeleteDocument = async (documentId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      setProjectDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      const message = (
        <>
          {t('document_moved_to') || 'Document moved to '}
          <Link
            to={`/projects/${params.projectId}/trash`}
            style={{
              color: '#ffffff',
              textDecoration: 'underline',
              fontWeight: 500,
            }}
            onClick={handleMobileNavigation}
          >
            {t('trash')}
          </Link>
        </>
      );
      showToast(message);

      window.dispatchEvent(new CustomEvent('document-deleted', {
        detail: { documentId, projectId: params.projectId },
      }));
    } catch (err) {
      console.error('Error deleting document:', err);
      showErrorToast(t('failed_to_delete_document') || 'Failed to delete document');
    }
  };

  const sortedDocuments = sortProjectExplorerDocuments(projectDocuments, project.mainDocumentId);
  const mainDocument = sortedDocuments.find((doc) => doc.id === project.mainDocumentId) ?? null;
  const otherDocuments = sortedDocuments.filter((doc) => doc.id !== project.mainDocumentId);
  const documentsListPath = `/projects/${params.projectId}/documents`;
  const MainDocumentIcon = mainDocument
    ? (mainDocument.icon ? getIconComponent(mainDocument.icon) : null) ?? BookText
    : null;

  const collapsedRailButtonClassName = '!h-[34px] !justify-center !text-center !gap-0';
  const ResourceIcon = PROJECT_SECTION_ICONS.resources;
  const TimelineIcon = PROJECT_SECTION_ICONS.timeline;
  const NotesIcon = PROJECT_SECTION_ICONS.notes;
  const TagIcon = PROJECT_SECTION_ICONS.tags;
  const CharacterIcon = PROJECT_SECTION_ICONS.characters;
  const LocationIcon = PROJECT_SECTION_ICONS.locations;
  const structureNavItems = [
    { key: 'storylines' as const, label: t('storylines'), Icon: PROJECT_SECTION_ICONS.storylines },
    { key: 'scenes' as const, label: t('scenes'), Icon: PROJECT_SECTION_ICONS.scenes },
    { key: 'chapters' as const, label: t('chapters'), Icon: PROJECT_SECTION_ICONS.chapters },
    { key: 'analysis' as const, label: t('Analysis'), Icon: PROJECT_SECTION_ICONS.analysis },
  ];

  if (isCollapsed) {
    return (
      <>
        <SidebarGroup className="pr-1 shrink-0">
          <SidebarGroupContent>
            <SidebarMenu className="items-center gap-1">
              {mainDocument && MainDocumentIcon && (
                  <SidebarMenuItem key={mainDocument.id}>
                    <SidebarMenuButton
                      tooltip={mainDocument.title}
                      isActive={location.pathname === `/projects/${params.projectId}/documents/${mainDocument.id}`}
                      onClick={navigateTo(`/projects/${params.projectId}/documents/${mainDocument.id}`)}
                      className={collapsedRailButtonClassName}
                    >
                      <MainDocumentIcon className="!h-5 !w-5" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
              )}
              {sortedDocuments.length > 0 && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t('more_documents') || 'More documents'}
                    isActive={
                      location.pathname === documentsListPath ||
                      otherDocuments.some(
                        (doc) => location.pathname === `/projects/${params.projectId}/documents/${doc.id}`,
                      )
                    }
                    onClick={navigateTo(documentsListPath)}
                    onAuxClick={navigateTo(documentsListPath)}
                    className={collapsedRailButtonClassName}
                  >
                    <PROJECT_SECTION_ICONS.documents className="!h-5 !w-5" />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="pr-1 shrink-0">
          <SidebarGroupContent>
            <SidebarMenu className="items-center gap-1">
              {sortedDocuments.length > 0 && (
                <SidebarMenuItem className="self-stretch">
                  <SidebarSeparator className="my-1" />
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={t('Resources')}
                  isActive={isPathActive(`/projects/${params.projectId}/resources`)}
                  onClick={navigateTo(`/projects/${params.projectId}/resources`)}
                  className={collapsedRailButtonClassName}
                >
                  <ResourceIcon className="!h-5 !w-5" />
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={t('Notes')}
                  isActive={isPathActive(`/projects/${params.projectId}/notes`)}
                  onClick={navigateTo(`/projects/${params.projectId}/notes`)}
                  className={cn(collapsedRailButtonClassName, 'relative')}
                >
                  <NotesIcon className="!h-5 !w-5" />
                  <CountBadge count={notesCount} mode="overlay" tone="neutral" maxDisplay={9} />
                </SidebarMenuButton>
              </SidebarMenuItem>
              {hasProjectTags && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={t('tag_manager_page_title')}
                    isActive={isPathActive(`/projects/${params.projectId}/tags`)}
                    onClick={navigateTo(`/projects/${params.projectId}/tags`)}
                    className={collapsedRailButtonClassName}
                  >
                    <TagIcon className="!h-5 !w-5" />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {sectionDocumentId && (
                <>
                  <SidebarMenuItem className="self-stretch">
                    <SidebarSeparator className="my-1" />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      tooltip={t('characters')}
                      isActive={isPathActive(getDocumentPanelPath(params.projectId, sectionDocumentId, 'characters'))}
                      onClick={navigateTo(getDocumentPanelPath(params.projectId, sectionDocumentId, 'characters'))}
                      className={collapsedRailButtonClassName}
                    >
                      <CharacterIcon className="!h-5 !w-5" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      tooltip={t('locations')}
                      isActive={isPathActive(getDocumentPanelPath(params.projectId, sectionDocumentId, 'locations'))}
                      onClick={navigateTo(getDocumentPanelPath(params.projectId, sectionDocumentId, 'locations'))}
                      className={collapsedRailButtonClassName}
                    >
                      <LocationIcon className="!h-5 !w-5" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={t('Timeline')}
                  isActive={isPathActive(`/projects/${params.projectId}/timeline`)}
                  onClick={navigateTo(`/projects/${params.projectId}/timeline`)}
                  className={collapsedRailButtonClassName}
                >
                  <TimelineIcon className="!h-5 !w-5" />
                </SidebarMenuButton>
              </SidebarMenuItem>

              {sectionDocumentId && (
                <>
                  <SidebarMenuItem className="self-stretch">
                    <SidebarSeparator className="my-1" />
                  </SidebarMenuItem>
                  {structureNavItems.map(({ key, label, Icon }) => {
                    const path = getDocumentPanelPath(params.projectId, sectionDocumentId, key);
                    return (
                      <SidebarMenuItem key={key}>
                        <SidebarMenuButton
                          tooltip={label}
                          isActive={isPathActive(path)}
                          onClick={navigateTo(path)}
                          className={collapsedRailButtonClassName}
                        >
                          <Icon className="!h-5 !w-5" />
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <TierLimitModal open={tierLimitModalOpen} onOpenChange={setTierLimitModalOpen} limitType={tierLimitType} />
        {canEditProject && params.projectId && (
          <CreateProjectNoteDialog
            projectId={params.projectId}
            open={isCreateNoteOpen}
            onOpenChange={setIsCreateNoteOpen}
          />
        )}
      </>
    );
  }

  return (
    <>
    <SidebarGroup className="pr-1">
      <SidebarGroupContent>
        <SidebarMenu className="mt-4 gap-3">
          <li>
            <Collapsible open={isFilesOpen} onOpenChange={setIsFilesOpen}>
              <div className="group/menu-item relative">
                <ExplorerSectionLabel
                  label={t('Documents')}
                  href={documentsListPath}
                  isExpanded={isFilesOpen}
                  isSectionActive={isPathActive(documentsListPath)}
                  onExpandedChange={setIsFilesOpen}
                  onNavigate={(event, href) => {
                    handleModifierAwareNavigate(event, navigate, href, {
                      afterSameTabNavigate: handleMobileNavigation,
                    });
                  }}
                />
                {canEditProject && (
                  <AddDocumentMenu
                    onCreate={() => {
                      void handleCreateNewDocument();
                    }}
                    onImport={handleImportFile}
                    isImporting={isImportingFile}
                    trigger={
                      <SidebarMenuAction
                        iconSize="sm"
                        showOnHover
                        aria-label={t('new document')}
                        title={t('new document')}
                      >
                        <Plus className="!h-4 !w-4" />
                      </SidebarMenuAction>
                    }
                  />
                )}
              </div>
              <CollapsibleContent>
                <SidebarMenu className="gap-0.5">
                  {sortedDocuments.map((doc) => {
                      const documentHref = `/projects/${params.projectId}/documents/${doc.id}`;
                      return (
                        <ProjectExplorerDocumentItem
                          key={doc.id}
                          doc={doc}
                          projectId={params.projectId}
                          mainDocumentId={project.mainDocumentId}
                          canEditProject={canEditProject}
                          isActive={
                            location.pathname === documentHref ||
                            location.pathname.startsWith(`${documentHref}/`)
                          }
                          onNavigate={(e, href) => {
                            handleModifierAwareNavigate(e, navigate, href, {
                              afterSameTabNavigate: handleMobileNavigation,
                            });
                          }}
                          onSetAsMainDocument={handleSetAsMainDocument}
                          onTogglePinned={handleToggleDocumentPinned}
                          onArchiveDocument={handleArchiveDocument}
                          onDeleteDocument={handleDeleteDocument}
                          t={t}
                        />
                      );
                    })}
                </SidebarMenu>
              </CollapsibleContent>
            </Collapsible>
          </li>

          <li>
            <Collapsible open={isDocumentationOpen} onOpenChange={setIsDocumentationOpen}>
              <ExplorerSectionLabel
                label={t('explorer_documentation_section')}
                href={`/projects/${params.projectId}/documentation`}
                isExpanded={isDocumentationOpen}
                isSectionActive={isPathActive(`/projects/${params.projectId}/documentation`)}
                onExpandedChange={setIsDocumentationOpen}
                onNavigate={(event, href) => {
                  handleModifierAwareNavigate(event, navigate, href, {
                    afterSameTabNavigate: handleMobileNavigation,
                  });
                }}
              />
              <CollapsibleContent>
                <SidebarMenu className="gap-0.5">
                  <SidebarMenuItem className="relative">
                    <SidebarMenuSubButton
                      className="cursor-pointer"
                      isActive={isPathActive(`/projects/${params.projectId}/resources`)}
                      onClick={(e) => {
                        handleModifierAwareNavigate(
                          e,
                          navigate,
                          `/projects/${params.projectId}/resources`,
                          { afterSameTabNavigate: handleMobileNavigation }
                        );
                      }}
                      onAuxClick={(e) => {
                        handleModifierAwareNavigate(
                          e,
                          navigate,
                          `/projects/${params.projectId}/resources`,
                          { afterSameTabNavigate: handleMobileNavigation }
                        );
                      }}
                    >
                      <ResourceIcon className="!h-4 !w-4 !text-secondary-foreground" />
                      <ExplorerLabelTooltip label={t('Resources')} />
                    </SidebarMenuSubButton>
                    {canEditProject && params.projectId && (
                      <ResourceCreateActions
                        projectId={params.projectId}
                        trigger={
                          <SidebarMenuAction
                            iconSize="sm"
                            showOnHover
                            aria-label={t('add_resource')}
                            title={t('add_resource')}
                          >
                            <Plus className="!h-4 !w-4" />
                          </SidebarMenuAction>
                        }
                      />
                    )}
                  </SidebarMenuItem>

                  <SidebarMenuItem className="relative">
                    <SidebarMenuSubButton
                      className="cursor-pointer"
                      isActive={isPathActive(`/projects/${params.projectId}/notes`)}
                      onClick={(e) => {
                        handleModifierAwareNavigate(
                          e,
                          navigate,
                          `/projects/${params.projectId}/notes`,
                          { afterSameTabNavigate: handleMobileNavigation }
                        );
                      }}
                      onAuxClick={(e) => {
                        handleModifierAwareNavigate(
                          e,
                          navigate,
                          `/projects/${params.projectId}/notes`,
                          { afterSameTabNavigate: handleMobileNavigation }
                        );
                      }}
                    >
                      <NotesIcon className="!h-4 !w-4 !text-secondary-foreground" />
                      <ExplorerLabelTooltip label={t('Notes')} />
                      <CountBadge
                        count={notesCount}
                        mode="inline"
                        tone="neutral"
                        maxDisplay={9}
                        className="mr-6"
                      />
                    </SidebarMenuSubButton>
                    {canEditProject && (
                      <SidebarMenuAction
                        iconSize="sm"
                        showOnHover
                        aria-label={t('create_note') || 'Create note'}
                        title={t('create_note') || 'Create note'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCreateNoteOpen(true);
                        }}
                      >
                        <Plus className="!h-4 !w-4" />
                      </SidebarMenuAction>
                    )}
                  </SidebarMenuItem>
                  {hasProjectTags && (
                    <SidebarMenuItem>
                      <SidebarMenuSubButton
                        className="cursor-pointer"
                        isActive={isPathActive(`/projects/${params.projectId}/tags`)}
                        onClick={(e) => {
                          handleModifierAwareNavigate(
                            e,
                            navigate,
                            `/projects/${params.projectId}/tags`,
                            { afterSameTabNavigate: handleMobileNavigation }
                          );
                        }}
                        onAuxClick={(e) => {
                          handleModifierAwareNavigate(
                            e,
                            navigate,
                            `/projects/${params.projectId}/tags`,
                            { afterSameTabNavigate: handleMobileNavigation }
                          );
                        }}
                      >
                        <TagIcon className="!h-4 !w-4 !text-secondary-foreground" />
                        <ExplorerLabelTooltip label={t('tag_manager_page_title')} />
                      </SidebarMenuSubButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </CollapsibleContent>
            </Collapsible>
          </li>

          <li>
            <Collapsible open={isWorldbuildingOpen} onOpenChange={setIsWorldbuildingOpen}>
              <ExplorerSectionLabel
                label={t('Worldbuilding')}
                href={`/projects/${params.projectId}/worldbuilding`}
                isExpanded={isWorldbuildingOpen}
                isSectionActive={isPathActive(`/projects/${params.projectId}/worldbuilding`)}
                onExpandedChange={setIsWorldbuildingOpen}
                onNavigate={(event, href) => {
                  handleModifierAwareNavigate(event, navigate, href, {
                    afterSameTabNavigate: handleMobileNavigation,
                  });
                }}
              />
              <CollapsibleContent>
                <SidebarMenu className="gap-0.5">
                  {sectionDocumentId && (
                    <>
                      <SidebarMenuItem>
                        <SidebarMenuSubButton
                          className="cursor-pointer"
                          isActive={isPathActive(getDocumentPanelPath(params.projectId, sectionDocumentId, 'characters'))}
                          onClick={navigateTo(getDocumentPanelPath(params.projectId, sectionDocumentId, 'characters'))}
                          onAuxClick={navigateTo(getDocumentPanelPath(params.projectId, sectionDocumentId, 'characters'))}
                        >
                          <CharacterIcon className="!h-4 !w-4 !text-secondary-foreground" />
                          <ExplorerLabelTooltip label={t('characters')} />
                        </SidebarMenuSubButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuSubButton
                          className="cursor-pointer"
                          isActive={isPathActive(getDocumentPanelPath(params.projectId, sectionDocumentId, 'locations'))}
                          onClick={navigateTo(getDocumentPanelPath(params.projectId, sectionDocumentId, 'locations'))}
                          onAuxClick={navigateTo(getDocumentPanelPath(params.projectId, sectionDocumentId, 'locations'))}
                        >
                          <LocationIcon className="!h-4 !w-4 !text-secondary-foreground" />
                          <ExplorerLabelTooltip label={t('locations')} />
                        </SidebarMenuSubButton>
                      </SidebarMenuItem>
                    </>
                  )}
                  <li>
                    <Collapsible open={isTimelineOpen} onOpenChange={setIsTimelineOpen}>
                      <div className="group/timeline-row relative">
                        <SidebarMenuSubButton
                          className="cursor-pointer"
                          isActive={isPathActive(`/projects/${params.projectId}/timeline`)}
                          onClick={(e) => {
                            if (timelineViews.length > 0) {
                              setIsTimelineOpen((prev) => !prev);
                            }
                            handleModifierAwareNavigate(
                              e,
                              navigate,
                              `/projects/${params.projectId}/timeline`,
                              { afterSameTabNavigate: handleMobileNavigation }
                            );
                          }}
                          onAuxClick={(e) => {
                            handleModifierAwareNavigate(
                              e,
                              navigate,
                              `/projects/${params.projectId}/timeline`,
                              { afterSameTabNavigate: handleMobileNavigation }
                            );
                          }}
                        >
                          <span
                            className="relative flex h-4 w-4 shrink-0 items-center justify-center"
                            role={timelineViews.length > 0 ? 'button' : undefined}
                            tabIndex={timelineViews.length > 0 ? 0 : undefined}
                            aria-expanded={timelineViews.length > 0 ? isTimelineOpen : undefined}
                            aria-label={
                              timelineViews.length > 0
                                ? (isTimelineOpen ? t('collapse') || 'Collapse' : t('expand') || 'Expand')
                                : undefined
                            }
                            onClick={
                              timelineViews.length > 0
                                ? (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setIsTimelineOpen((prev) => !prev);
                                  }
                                : undefined
                            }
                            onKeyDown={
                              timelineViews.length > 0
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setIsTimelineOpen((prev) => !prev);
                                    }
                                  }
                                : undefined
                            }
                          >
                            <TimelineIcon
                              className={cn(
                                '!h-4 !w-4 !text-secondary-foreground',
                                timelineViews.length > 0 && 'group-hover/timeline-row:opacity-0'
                              )}
                            />
                            {timelineViews.length > 0 && (
                              isTimelineOpen ? (
                                <ChevronUp className="!h-4 !w-4 !text-secondary-foreground absolute inset-0 opacity-0 transition-opacity group-hover/timeline-row:opacity-100" />
                              ) : (
                                <ChevronDown className="!h-4 !w-4 !text-secondary-foreground absolute inset-0 opacity-0 transition-opacity group-hover/timeline-row:opacity-100" />
                              )
                            )}
                          </span>
                          <ExplorerLabelTooltip label={t('Timeline')} />
                        </SidebarMenuSubButton>
                        {timelineViews.length > 0 && (
                          <CollapsibleContent>
                            <SidebarMenuSub className={nestedExplorerMenuSubClassName}>
                              {timelineViews.map((view) => (
                                <SidebarMenuSubItem key={view.id}>
                                  <SidebarMenuSubButton
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                      handleModifierAwareNavigate(
                                        e,
                                        navigate,
                                        `/projects/${params.projectId}/timeline?viewId=${view.id}`,
                                        { afterSameTabNavigate: handleMobileNavigation }
                                      );
                                    }}
                                    onAuxClick={(e) => {
                                      handleModifierAwareNavigate(
                                        e,
                                        navigate,
                                        `/projects/${params.projectId}/timeline?viewId=${view.id}`,
                                        { afterSameTabNavigate: handleMobileNavigation }
                                      );
                                    }}
                                  >
                                    <ListFilter className="!h-4 !w-4 !text-secondary-foreground" />
                                    <ExplorerLabelTooltip label={view.name} />
                                  </SidebarMenuSubButton>
                                  {canEditProject && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <SidebarMenuAction iconSize="sm" showOnHover>
                                        <MoreVertical className="!h-4 !w-4" />
                                      </SidebarMenuAction>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteTimelineView(view.id);
                                        }}
                                        className="group"
                                      >
                                        <RotatingDeleteIcon className="mr-2" />
                                        {t('delete')}
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                  )}
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        )}
                      </div>
                    </Collapsible>
                  </li>
                </SidebarMenu>
              </CollapsibleContent>
            </Collapsible>
          </li>

          {sectionDocumentId && (
            <li>
              <Collapsible open={isStructureOpen} onOpenChange={setIsStructureOpen}>
                <ExplorerSectionLabel
                  label={t('Structure')}
                  href={`/projects/${params.projectId}/structure`}
                  isExpanded={isStructureOpen}
                  isSectionActive={isPathActive(`/projects/${params.projectId}/structure`)}
                  onExpandedChange={setIsStructureOpen}
                  onNavigate={(event, href) => {
                    handleModifierAwareNavigate(event, navigate, href, {
                      afterSameTabNavigate: handleMobileNavigation,
                    });
                  }}
                />
                <CollapsibleContent>
                  <SidebarMenu className="gap-0.5">
                    {structureNavItems.map(({ key, label, Icon }) => {
                      const path = getDocumentPanelPath(params.projectId, sectionDocumentId, key);
                      return (
                        <SidebarMenuItem key={key}>
                          <SidebarMenuSubButton
                            className="cursor-pointer"
                            isActive={isPathActive(path)}
                            onClick={navigateTo(path)}
                            onAuxClick={navigateTo(path)}
                          >
                            <Icon className="!h-4 !w-4 !text-secondary-foreground" />
                            <ExplorerLabelTooltip label={label} />
                          </SidebarMenuSubButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </CollapsibleContent>
              </Collapsible>
            </li>
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
    <TierLimitModal open={tierLimitModalOpen} onOpenChange={setTierLimitModalOpen} limitType={tierLimitType} />
    {canEditProject && params.projectId && (
      <CreateProjectNoteDialog
        projectId={params.projectId}
        open={isCreateNoteOpen}
        onOpenChange={setIsCreateNoteOpen}
      />
    )}
    </>
  );
};

